import clsx from 'clsx';
import React, { MutableRefObject, Ref, useState } from 'react';
import { EditorModes, FormatterModes } from '../../../../code/interfaces/editor';
import { RbacPermissions } from '../../../../core/interfaces/user';
import { AccountAccessService } from '../../../../core/services/account-access.service';
import useHasPermission from '../../../../access-control/hooks/useHasPermission';
import useKeyboardShortcutListener from '../../../../a11y/shortcuts/hooks/useKeyboardShortcutListener';
import useService from '../../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import QueryEditor, { QueryChangeFunction } from '../../../../code/components/query-editor/QueryEditor';
import { ElapseTimer } from '../../../../date-time/components/elapse-timer/ElaspeTimer';
import { HdIcon, HdIconButton, HdRbacButton, HdTooltip } from '../../../../components';
import HdDuration from '../../../../date-time/components/duration/HdDuration';
import HdShortcut from '../../../../a11y/shortcuts/components/shortcut/HdShortcut';
import {
  QueryEditorBadgeType,
  QueryExecutionStatusEnum,
  WorkbenchQueryEditorConfiguration
} from '../../interfaces/workbench';
import styles from './WorkbenchQueryEditorContainer.module.scss';

export interface WorkbenchQueryEditorProps {
  className?: string;
  showingInsideCard?: boolean;
  query: string;
  onQueryChange: QueryChangeFunction;
  queryError?: any;
  queryOutput?: any;
  runningQuery: boolean;
  queryExecutionStatus: QueryExecutionStatusEnum;
  lastRunActionTime: number;
  elapsedTimerInitialValue?: number;
  runQuery: Function;
  onRunQueryClick?: Function;
  stopRunningQuery: Function;
  queryEditorProps: WorkbenchQueryEditorConfiguration;
  elapsedTimerRef?: Ref<any>;
  editorRef?: MutableRefObject<any>;
  sqlEditorMode?: EditorModes;
  sqlFormatterMode?: FormatterModes;
  formatterEnabled?: boolean;
  rbacPermission?: RbacPermissions;
  runQueryShortcutCommand: string;
}

export interface CollapsibleWorkbenchQueryEditorProps extends WorkbenchQueryEditorProps {
  isExpandedView?: boolean;
  toggleExpandedView: Function;
}

export function CollapsibleWorkbenchQueryEditor({
  className = '',
  showingInsideCard,
  query,
  onQueryChange,
  queryError,
  queryOutput,
  runningQuery,
  runQuery,
  onRunQueryClick,
  stopRunningQuery,
  queryExecutionStatus,
  lastRunActionTime,
  elapsedTimerInitialValue,
  isExpandedView,
  toggleExpandedView,
  queryEditorProps = {},
  elapsedTimerRef,
  editorRef,
  sqlEditorMode = EditorModes.SQL,
  sqlFormatterMode = FormatterModes.SQL,
  formatterEnabled = true,
  rbacPermission,
  runQueryShortcutCommand
}: CollapsibleWorkbenchQueryEditorProps) {
  const {
    focusEditorOnLoad,
    runQueryText = 'Run Query',
    runningQueryText = 'Stop Running Query',
    showProcessedRowsCount,
    elapsedTimerEnabled,
    queryEditorBadgeType,
    draftQueryTooltipMessage,
    QueryEditorHeaderActions,
    QueryEditorFooterActions,
    queryTooltipMessage
  } = queryEditorProps;

  const showElapsedTimer = queryExecutionStatus && ![
    QueryExecutionStatusEnum.IDLE,
    QueryExecutionStatusEnum.CANCELLED,
    QueryExecutionStatusEnum.ERRORED
  ].includes(queryExecutionStatus);

  const isQueryEditorBadgeDraft = QueryEditorBadgeType.DRAFT_QUERY === queryEditorBadgeType;
  const isQueryEditorBadgeUnverified = QueryEditorBadgeType.UNVERIFIED_CHANGES === queryEditorBadgeType;
  const isQueryEditorBadgeUnsavedChanges = QueryEditorBadgeType.UNSAVED_CHANGES === queryEditorBadgeType;

  const [ runMode, setRunMode ] = useState<'click' | 'program'>();

  const runQueryClick = () => {
    setRunMode('click');

    if (isExpandedView) {
      toggleExpandedView();
    }

    if (typeof onRunQueryClick === 'function') {
      onRunQueryClick();
    }

    runningQuery ? stopRunningQuery() : runQuery();
  };

  const runQueryInProgramMode = () => {
    setRunMode('program');

    runQuery();
  };

  const accountAccessService = useService(AccountAccessService);
  const showUserAccessError = !accountAccessService.getAccountAccessConsentStatus();

  const { hasPermission } = useHasPermission();

  useKeyboardShortcutListener(runQueryClick, runQueryShortcutCommand, {
    priority: 0,
    terminal: 'match'
  });

  const boxClasses = clsx(
    'box',
    showingInsideCard ? 'box--inside-card border-left-0 border-right-0' : '',
    className
  );

  const dataIdGenerator = getDataIdGenerator('workbench-query-editor');

  return (
    <div className={boxClasses}>
      <div className='box__header'>
        <div className='box__title'>
          SQL Query

          {!!queryTooltipMessage ? (
            <HdTooltip title={queryTooltipMessage}>
              <HdIcon name='info' className='info-icon' />
            </HdTooltip>
          ) : null}

          {isQueryEditorBadgeUnsavedChanges ? (
            <span className='hd-faded-label hd-faded-label-sm hd-faded-label-warning-thin ml-4'>
              Unsaved Changes
            </span>
          ) : null}
        </div>

        <div className='box__header__actions'>
          {isQueryEditorBadgeDraft ? (
            <span className='badge badge-solid badge-solid-md'>
              Draft

              {draftQueryTooltipMessage ? (
                <HdTooltip title={draftQueryTooltipMessage}>
                  <HdIcon name='info' className='ml-1' size={1} />
                </HdTooltip>
              ) : null}
            </span>
          ) : null}

          {!!QueryEditorHeaderActions ? (
            <QueryEditorHeaderActions
              dataId={dataIdGenerator('header-actions')}
              query={query}
              isExpandedView={isExpandedView}
            />
          ) : null}

          <div className='filter-separator' />

          <HdTooltip title={isExpandedView ? 'Collapse Editor' : 'Expand Editor'}>
            <HdIconButton
              dataId={dataIdGenerator('toggle-expand')}
              onClick={() => toggleExpandedView()}
            >
              <HdIcon name={isExpandedView ? 'small-screen' : 'full-screen'} />
            </HdIconButton>
          </HdTooltip>
        </div>
      </div>

      <div className='box__body'>
        <QueryEditor
          dataId={dataIdGenerator('')}
          className={styles.queryEditor}
          value={query}
          error={queryError}
          onChange={onQueryChange}
          focusEditorOnLoad={focusEditorOnLoad}
          mode={sqlEditorMode}
          allowFormatting={formatterEnabled}
          formatOptions={{
            language: sqlFormatterMode
          }}
          getCodeMirror={(ed) => {
            if (editorRef) {
              editorRef.current = ed;
            }
          }}
          showUserAccessError={showUserAccessError}
          showRbacAccessError={!hasPermission(rbacPermission)}
        />

        <div className='center-flex-row justify-between mt-5'>
          <div className='center-flex-row'>
            {elapsedTimerEnabled ? (
              <div className={!showElapsedTimer ? 'd-none' : ''}>
                Elapsed:{' '}
                <span className='text-medium'>
                  <ElapseTimer
                    ref={elapsedTimerRef}
                    initValue={elapsedTimerInitialValue}
                    useAbbr={true}
                  />
                </span>
              </div>
            ) : null}

            {isQueryEditorBadgeUnverified ? (
              <div className={`badge badge-solid-md ml-2 ${styles.unverifiedChangesWarning}`}>
                Unverified Changes
              </div>
            ) : null}
          </div>

          <div className='center-flex-row'>
            {showProcessedRowsCount && queryOutput && !runningQuery ? (
              <>
                Rows Processed
                <HdTooltip
                  title='While testing the query, Hevo automatically limits the query to 100 records.'
                  placement='right-start'
                >
                  <HdIcon name='info' className='info-icon' />
                </HdTooltip>
                {' '}:
                <span className='text-medium ml-1'>{queryOutput.rows.length}</span>
              </>
            ) : null}
          </div>

          <div className='center-flex-row'>
            {lastRunActionTime ? (
              <span className='mr-6'>
                Run Time:{' '}
                <span className='text-medium'>
                  <HdDuration
                    duration={ lastRunActionTime }
                    options={ { useAbbr: true } }
                  />
                </span>
              </span>
            ) : null}

            <HdRbacButton
              dataId={dataIdGenerator('run-query')}
              variation='outline'
              icon={runningQuery && runMode === 'click' ? 'testing' : 'play'}
              disabled={!query || runningQuery && runMode !== 'click'}
              tooltipContent={<HdShortcut command={runQueryShortcutCommand} />}
              onClick={runQueryClick}
              rbacPermission={rbacPermission}
            >
              {runningQuery && runMode === 'click' ? runningQueryText : runQueryText}
            </HdRbacButton>

            {!!QueryEditorFooterActions ? (
              <QueryEditorFooterActions
                dataId={dataIdGenerator('footer-actions')}
                query={query}
                runningQuery={runningQuery}
                runQuery={runQueryInProgramMode}
                stopRunningQuery={stopRunningQuery}
                isExpandedView={isExpandedView}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
